import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import LockClockIcon from '@mui/icons-material/LockClock';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalculateIcon from '@mui/icons-material/Calculate';
import ViewListIcon from '@mui/icons-material/ViewList';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { Roles } from '../constants';

/** Reserved AE portal shape — AccountExecutive only so Admin/LenderAdmin lender nav stays clean. */
const AE_PORTAL_ROLES = [Roles.AccountExecutive];

/**
 * Master menu item definitions.
 * Each item specifies which roles can see it and an optional featureFlag key
 * that must be enabled for the item to appear.
 */
const allMenuItems = [
  // ── Lender / Admin ──
  { id: 'lender-dashboard', text: 'Dashboard', railLabel: 'Home', icon: DashboardIcon, path: '/lender/dashboard', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Overview' },
  { id: 'ae-dashboard', text: 'AE Dashboard', railLabel: 'AE Dash', icon: BarChartIcon, path: '/ae/dashboard', roles: [Roles.Admin, Roles.LenderAdmin, Roles.LoanOfficer, Roles.AccountExecutive], group: 'Overview' },
  { id: 'ae-scorecard', text: 'Scorecard', railLabel: 'Scorecard', icon: EmojiEventsOutlinedIcon, path: '/ae/scorecard', roles: AE_PORTAL_ROLES, group: 'Overview' },
  { id: 'ae-brokers', text: 'Book of Business', railLabel: 'Brokers', icon: PeopleAltOutlinedIcon, path: '/ae/brokers', roles: AE_PORTAL_ROLES, group: 'Book' },
  { id: 'ae-prospects', text: 'Prospects', railLabel: 'Prospects', icon: PersonSearchOutlinedIcon, path: '/ae/prospects', roles: AE_PORTAL_ROLES, group: 'Recruiting' },
  { id: 'ae-marketing', text: 'Marketing', railLabel: 'Marketing', icon: CampaignOutlinedIcon, path: '/ae/marketing', roles: AE_PORTAL_ROLES, group: 'Marketing' },
  { id: 'ae-resources', text: 'Resources', railLabel: 'Resources', icon: MenuBookOutlinedIcon, path: '/ae/resources', roles: AE_PORTAL_ROLES, group: 'Resources' },
  { id: 'lender-scenario-pricer', text: 'Quick Pricer', railLabel: 'Pricer', icon: CalculateIcon, path: '/lender/scenario-pricer', roles: [Roles.Admin, Roles.LenderAdmin], featureFlag: 'scenarioPricer', group: 'Loans' },
  { id: 'lender-brokers', text: 'Broker Companies', railLabel: 'Brokers', icon: BusinessIcon, path: '/lender/brokers', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Organization' },
  { id: 'lender-comp-requests', text: 'Comp Requests', railLabel: 'Comp Req', icon: PriceCheckIcon, path: '/lender/compensation-requests', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Organization' },
  { id: 'lender-users', text: 'Users', railLabel: 'Users', icon: GroupIcon, path: '/lender/users', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Organization' },
  { id: 'lender-retail-pipeline', text: 'Retail Pipeline', railLabel: 'Retail', icon: HomeWorkIcon, path: '/lender/retail-pipeline', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Loans' },
  { id: 'lender-vesta-contacts', text: 'UFF Contact Sync', railLabel: 'Sync', icon: SyncAltIcon, path: '/lender/vesta-contacts', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Admin' },
  { id: 'lender-marketing', text: 'Marketing', railLabel: 'Marketing', icon: CampaignOutlinedIcon, path: '/lender/marketing', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Admin' },
  { id: 'lender-lock-desk', text: 'Lock Desk', railLabel: 'Lock Desk', icon: LockClockIcon, path: '/lender/lock-desk', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Admin' },
  { id: 'lender-branding', text: 'Branding', railLabel: 'Branding', icon: PaletteOutlinedIcon, path: '/lender/branding', roles: [Roles.Admin, Roles.LenderAdmin], group: 'Admin' },
  { id: 'lender-feature-management', text: 'Feature Management', railLabel: 'Features', icon: ToggleOnIcon, path: '/lender/feature-management', roles: [Roles.Admin], group: 'Admin' },

  // ── Account Executive (role-specific loan tools) ──
  { id: 'ae-pipeline', text: 'AE Pipeline', railLabel: 'Pipeline', icon: ListAltIcon, path: '/ae/pipeline', roles: [Roles.LoanOfficer, Roles.AccountExecutive], group: 'Loans' },
  { id: 'ae-scenario-pricer', text: 'Quick Pricer', railLabel: 'Pricer', icon: CalculateIcon, path: '/ae/scenario-pricer', roles: [Roles.LoanOfficer, Roles.AccountExecutive], featureFlag: 'scenarioPricer', group: 'Loans' },

  // ── Broker ──
  { id: 'broker-scenario-pricer', text: 'Quick Pricer', railLabel: 'Pricer', icon: CalculateIcon, path: '/broker/scenario-pricer', roles: [Roles.BrokerAdmin, Roles.Broker, Roles.Processor, Roles.BrokerFullAccessReadOnly], featureFlag: 'scenarioPricer', group: 'Loans' },
  { id: 'broker-pipeline', text: 'Pipeline', railLabel: 'Pipeline', icon: ViewListIcon, path: '/broker/pipeline', roles: [Roles.BrokerAdmin, Roles.Broker, Roles.Processor, Roles.BrokerFullAccessReadOnly], group: 'Loans' },
  { id: 'broker-company', text: 'Manage Company', railLabel: 'Company', icon: ApartmentIcon, path: '/broker/company', roles: [Roles.BrokerAdmin], group: 'Organization' },
  { id: 'broker-company-view', text: 'Company', railLabel: 'Company', icon: ApartmentIcon, path: '/broker/company', roles: [Roles.Processor], group: 'Organization' },
  { id: 'broker-compensation', text: 'Compensation', railLabel: 'Comp', icon: AttachMoneyIcon, path: '/broker/compensation', roles: [Roles.BrokerAdmin], group: 'Organization' },
];

/** Group display order for the sidebar. */
export const MENU_GROUP_ORDER = ['Overview', 'Book', 'Recruiting', 'Loans', 'Marketing', 'Resources', 'Organization', 'Admin'];

/**
 * Returns the filtered menu items for the given role.
 * @param {string} role - The user's role string (e.g. 'Admin', 'BrokerAdmin')
 * @param {Object} featureFlags - Feature flag map from the configuration API
 * @returns {Array} Menu items the user should see
 */
export const getMenuItemsForRole = (role, featureFlags = {}, options = {}) => {
  const { pendingBroker = false } = options;
  return allMenuItems.filter((item) => {
    if (!item.roles.includes(role)) return false;
    if (item.featureFlag && featureFlags[item.featureFlag] !== true) return false;
    if (pendingBroker && item.id !== 'broker-scenario-pricer') return false;
    return true;
  });
};

/**
 * Human-readable role labels for the user dropdown.
 */
export const roleLabels = {
  [Roles.Admin]: 'Administrator',
  [Roles.LenderAdmin]: 'Lender Admin',
  [Roles.LoanOfficer]: 'Account Executive',
  [Roles.BrokerAdmin]: 'Broker Admin',
  [Roles.Broker]: 'Loan Officer',
  [Roles.Processor]: 'Processor',
  [Roles.BrokerFullAccessReadOnly]: 'Read-only',
  [Roles.AccountExecutive]: 'Account Executive',
};
